import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper, Typography, Box, Button, Chip, Alert, CircularProgress, Divider,
  Autocomplete, TextField, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import EmptyState from '../EmptyState';

// Who gets which reminder.
//
// Reminders are routed to user GROUPS, not to typed-in addresses: the address is read from
// the user record when the reminder is sent, so someone changing their email changes it in
// one place instead of in their profile and again in every reminder that named them.
//
// The screen shows configuration AND consequence side by side. "Which groups are selected"
// and "who will actually be emailed" are different questions — a group with nobody active
// in it looks correctly set up and delivers nothing — so the resolved list is always on
// screen next to the selectors.
const ReminderRouting = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [draft, setDraft] = useState({});
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [routing, userList] = await Promise.all([
        axios.get('/reminder-routing'),
        axios.get('/users')
      ]);
      setData(routing.data);
      const all = Array.isArray(userList.data) ? userList.data : (userList.data.data || []);
      setUsers(all.filter((u) => u.isActive !== false && u.role !== 'tenant'));

      const seeded = {};
      for (const type of routing.data.types) {
        seeded[type.key] = {
          roleNames: type.subscriptions.filter((s) => s.recipientType === 'role').map((s) => s.roleName),
          userIds: type.subscriptions.filter((s) => s.recipientType === 'user').map((s) => s.userId)
        };
      }
      setDraft(seeded);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load reminder routing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (typeKey) => {
    setSaving(typeKey);
    try {
      const { data: result } = await axios.put(`/reminder-routing/${typeKey}`, draft[typeKey]);
      // The server reports how many people this actually reaches, which is not always what
      // the selection suggests — so it is surfaced rather than a bare "Saved".
      if (result.deliversTo === 0) toast.warning(result.message);
      else toast.success(result.message);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save');
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <Box p={4} textAlign="center"><CircularProgress /></Box>;
  if (!data) return <EmptyState title="Reminder routing unavailable" description="Try reloading the page." />;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Reminder routing</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose which user groups receive each reminder. Email addresses are read from user
        records when the reminder is sent — change someone&apos;s email in User Management and
        every reminder they receive follows automatically.
      </Typography>

      {data.types.map((type) => {
        const d = draft[type.key] || { roleNames: [], userIds: [] };
        return (
          <Accordion key={type.key} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={2} width="100%" pr={2}>
                <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{type.label}</Typography>
                <Chip
                  size="small"
                  color={type.deliversTo > 0 ? 'success' : 'error'}
                  variant="outlined"
                  label={type.deliversTo > 0 ? `${type.deliversTo} recipient(s)` : 'Nobody'}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {type.description}
              </Typography>

              <Box display="flex" gap={2} flexWrap="wrap">
                <Autocomplete
                  multiple
                  sx={{ minWidth: 320, flex: 1 }}
                  options={data.roles}
                  value={data.roles.filter((r) => d.roleNames.includes(r.name))}
                  getOptionLabel={(r) => `${r.displayName} (${r.activeUsers} active)`}
                  isOptionEqualToValue={(o, v) => o.name === v.name}
                  onChange={(_, vals) => setDraft({
                    ...draft,
                    [type.key]: { ...d, roleNames: vals.map((v) => v.name) }
                  })}
                  renderInput={(params) => <TextField {...params} label="User groups" />}
                />
                <Autocomplete
                  multiple
                  sx={{ minWidth: 320, flex: 1 }}
                  options={users}
                  value={users.filter((u) => d.userIds.includes(u._id || u.id))}
                  getOptionLabel={(u) => `${u.name} (${u.role})`}
                  isOptionEqualToValue={(o, v) => (o._id || o.id) === (v._id || v.id)}
                  onChange={(_, vals) => setDraft({
                    ...draft,
                    [type.key]: { ...d, userIds: vals.map((v) => v._id || v.id) }
                  })}
                  renderInput={(params) => (
                    <TextField {...params} label="Specific people (optional)"
                      helperText="For when one named person should get it, not a whole group" />
                  )}
                />
              </Box>

              {type.emptyRoles?.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No active user holds {type.emptyRoles.join(', ')} — this reminder will not
                  reach anyone through {type.emptyRoles.length > 1 ? 'those groups' : 'that group'}.
                </Alert>
              )}

              {type.legacyAddresses?.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Also sent to {type.legacyAddresses.join(', ')} — {' '}
                  addresses configured before routing existed, kept so nothing stopped arriving.
                  Create a user for each and subscribe them above, then clear the old setting.
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Currently reaches</Typography>
              {type.resolvedRecipients.length === 0 && !(type.legacyAddresses?.length) ? (
                <Alert severity="warning" sx={{ mt: 0 }}>
                  Nobody. This reminder will not be sent until you select a user group or a
                  named person and save.
                </Alert>
              ) : type.resolvedRecipients.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No subscribed users — only the legacy addresses above.
                </Typography>
              ) : (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {type.resolvedRecipients.map((r) => (
                    <Chip
                      key={r.email}
                      size="small"
                      label={`${r.name} · ${r.email}`}
                      title={r.reasons.join(', ')}
                    />
                  ))}
                </Box>
              )}

              <Box mt={2}>
                <Button
                  variant="contained"
                  disabled={saving === type.key}
                  onClick={() => save(type.key)}
                >
                  {saving === type.key ? 'Saving…' : 'Save'}
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Paper>
  );
};

export default ReminderRouting;
